const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const safeJsonParse = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return null;
};

/**
 * Extract the canonical 1-based canopy number when an identifier follows the
 * standard canopy naming convention (e.g. "Canopy 01", "Canopy 2", "canopy-01").
 */
export const canonicalCanopyNumber = (value) => {
  if (value === null || value === undefined) return null;
  const match = String(value).trim().match(/^canopy[\s_-]*0*(\d+)$/i);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const candidateIdsFromRecord = (record) => {
  if (!isObject(record)) return [record].filter(Boolean).map(String);
  const nested = safeJsonParse(record.payload ?? record.data);
  const body = isObject(nested) ? nested : {};
  return [
    record.canopyId,
    record.canopy_id,
    record.deviceId,
    record.device_id,
    body.canopyId,
    body.canopy_id,
    body.deviceId,
    body.device_id,
  ]
    .filter((item) => item !== undefined && item !== null && item !== '')
    .map((item) => String(item).trim());
};

/**
 * Check whether a telemetry record or ID string matches the requested canopy ID.
 * Prefers exact (case-insensitive) match, with fallback to canonical canopy number
 * so "Canopy 01", "Canopy 1", and "canopy-01" resolve cleanly.
 */
export const matchesCanopyId = (recordOrId, targetCanopyId, { allowNumberFallback = true } = {}) => {
  if (!targetCanopyId) return false;
  const target = String(targetCanopyId).trim().toLowerCase();
  const candidates = candidateIdsFromRecord(recordOrId);
  if (candidates.some((candidate) => candidate.toLowerCase() === target)) {
    return true;
  }
  if (!allowNumberFallback) return false;
  const targetNumber = canonicalCanopyNumber(targetCanopyId);
  if (targetNumber === null) return false;
  return candidates.some((candidate) => canonicalCanopyNumber(candidate) === targetNumber);
};

const unwrapEnvelope = (input) => {
  let current = safeJsonParse(input);
  let depth = 0;
  while (isObject(current) && depth < 5) {
    if (
      current.success === false
      || current.status === 'error'
      || (typeof current.statusCode === 'number' && current.statusCode >= 400)
      || (current.error && !current.data && !current.payload && !current.sabalController)
    ) {
      return null;
    }
    if (current.success === true && current.data !== undefined) {
      current = safeJsonParse(current.data);
      depth += 1;
      continue;
    }
    if (current.status === 'success' && current.data !== undefined) {
      current = safeJsonParse(current.data);
      depth += 1;
      continue;
    }
    break;
  }
  return current;
};

const isCanopyTelemetryRecord = (record) => {
  if (!isObject(record)) return false;
  const type = String(record.messageType || record.message_type || record.type || record.topic || '').toLowerCase();
  if (type.includes('canopy') && !type.includes('equipment')) return true;
  const payload = safeJsonParse(record.payload ?? record.data);
  return Boolean(
    record.sabalController
    || (isObject(payload) && (payload.sabalController || payload.data?.sabalController))
  );
};

const recordTimestampMs = (record) => {
  if (!isObject(record)) return -Infinity;
  const payload = safeJsonParse(record.payload ?? record.data);
  const body = isObject(payload) ? payload : {};
  const ts = firstDefined(
    record.timestamp,
    record.observedAt,
    record.observed_at,
    body.timestamp,
    body.observedAt,
    record.receivedAt,
    record.received_at,
    record.createdAt,
    record.created_at
  );
  const parsed = ts ? Date.parse(ts) : NaN;
  return Number.isFinite(parsed) ? parsed : -Infinity;
};

/**
 * Normalize a single telemetry record from any supported source:
 * - Legacy `/api/telemetry/latest/:deviceId` (snake_case or camelCase, envelope or row)
 * - Energy v2 `/api/energy/latest` (`energy_current` / `energy_message` row with `payload`)
 * - Real-time WebSocket `telemetry_update` event (`{ event_type, data }` or row)
 *
 * Returns `null` if the input is empty or represents an error response.
 */
export const normalizeTelemetryRecord = (input, fallbackIdentity = {}) => {
  if (!input) return null;

  // Handle WebSocket wrapper `{ event_type: 'INSERT', table: 'telemetry', data: <row> }`
  const wsUnwrapped = isObject(input) && (input.event_type || input.eventType) && input.data
    ? input.data
    : input;

  const unwrapped = unwrapEnvelope(wsUnwrapped);
  if (!unwrapped) return null;

  if (Array.isArray(unwrapped)) {
    return extractCanopyTelemetryFromEnergyLatest(unwrapped, fallbackIdentity.canopyId, fallbackIdentity);
  }

  if (!isObject(unwrapped)) return null;

  // Reject error objects that might have slipped through without `status === 'error'`
  if (
    unwrapped.status === 'error'
    || (typeof unwrapped.statusCode === 'number' && unwrapped.statusCode >= 400)
    || (unwrapped.error && !unwrapped.data && !unwrapped.payload && !unwrapped.sabalController)
  ) {
    return null;
  }

  const rawInner = safeJsonParse(unwrapped.payload ?? unwrapped.data);
  const innerObj = isObject(rawInner) ? rawInner : {};
  const nestedData = safeJsonParse(innerObj.data ?? innerObj.payload);
  const nestedObj = isObject(nestedData) ? nestedData : {};

  const sabalController = firstDefined(
    unwrapped.sabalController,
    innerObj.sabalController,
    nestedObj.sabalController
  );

  const fleetId = firstDefined(
    unwrapped.fleet_id,
    unwrapped.fleetId,
    innerObj.fleet_id,
    innerObj.fleetId,
    nestedObj.fleet_id,
    nestedObj.fleetId,
    fallbackIdentity.fleetId
  );
  const customerId = firstDefined(
    unwrapped.customer_id,
    unwrapped.customerId,
    innerObj.customer_id,
    innerObj.customerId,
    nestedObj.customer_id,
    nestedObj.customerId,
    fallbackIdentity.customerId
  );
  const siteId = firstDefined(
    unwrapped.site_id,
    unwrapped.siteId,
    innerObj.site_id,
    innerObj.siteId,
    nestedObj.site_id,
    nestedObj.siteId,
    fallbackIdentity.siteId
  );
  const canopyId = firstDefined(
    unwrapped.canopy_id,
    unwrapped.canopyId,
    innerObj.canopy_id,
    innerObj.canopyId,
    nestedObj.canopy_id,
    nestedObj.canopyId,
    unwrapped.device_id,
    unwrapped.deviceId,
    innerObj.device_id,
    innerObj.deviceId,
    fallbackIdentity.canopyId
  );
  const deviceId = firstDefined(
    unwrapped.device_id,
    unwrapped.deviceId,
    innerObj.device_id,
    innerObj.deviceId,
    nestedObj.device_id,
    nestedObj.deviceId,
    canopyId,
    fallbackIdentity.deviceId
  );
  const messageId = firstDefined(
    unwrapped.message_id,
    unwrapped.messageId,
    innerObj.message_id,
    innerObj.messageId,
    nestedObj.message_id,
    nestedObj.messageId
  );
  const rawSequenceNumber = firstDefined(
    unwrapped.sequence_number,
    unwrapped.sequenceNumber,
    innerObj.sequence_number,
    innerObj.sequenceNumber,
    nestedObj.sequence_number,
    nestedObj.sequenceNumber
  );
  const parsedSeq = rawSequenceNumber !== null && rawSequenceNumber !== ''
    ? Number(rawSequenceNumber)
    : NaN;
  const sequenceNumber = Number.isFinite(parsedSeq) ? parsedSeq : rawSequenceNumber;
  const timestamp = firstDefined(
    unwrapped.timestamp,
    innerObj.timestamp,
    nestedObj.timestamp,
    unwrapped.observedAt,
    unwrapped.observed_at,
    innerObj.observedAt,
    innerObj.observed_at,
    sabalController?.systemInfo?.lastUpdateTime
  );
  const createdAt = firstDefined(
    unwrapped.created_at,
    unwrapped.createdAt,
    unwrapped.receivedAt,
    unwrapped.received_at,
    innerObj.created_at,
    innerObj.createdAt,
    innerObj.receivedAt,
    innerObj.received_at,
    timestamp
  );
  const id = firstDefined(
    unwrapped.id,
    unwrapped.energyMessageId,
    unwrapped.energy_message_id,
    innerObj.id,
    innerObj.energyMessageId,
    innerObj.energy_message_id,
    messageId
  );
  const canopyName = firstDefined(
    unwrapped.canopyName,
    unwrapped.canopy_name,
    innerObj.canopyName,
    innerObj.canopy_name,
    innerObj.name,
    fallbackIdentity.canopyName
  );

  // Must have either sabalController or a valid device/canopy/message identifier
  const hasTelemetryContent = Boolean(
    sabalController
    || unwrapped.device_id
    || unwrapped.deviceId
    || unwrapped.canopy_id
    || unwrapped.canopyId
    || unwrapped.energyMessageId
    || unwrapped.energy_message_id
    || (isObject(rawInner) && Object.keys(rawInner).length > 0)
  );
  if (!hasTelemetryContent) {
    return null;
  }

  const normalizedPayload = isObject(rawInner)
    ? {
      ...rawInner,
      ...(sabalController ? { sabalController } : {}),
    }
    : (sabalController ? { sabalController } : {});

  return {
    ...unwrapped,
    id,
    fleet_id: fleetId,
    fleetId,
    customer_id: customerId,
    customerId,
    site_id: siteId,
    siteId,
    device_id: deviceId,
    deviceId,
    canopy_id: canopyId,
    canopyId,
    ...(canopyName ? { canopyName, canopy_name: canopyName } : {}),
    message_id: messageId,
    messageId,
    sequence_number: sequenceNumber,
    sequenceNumber,
    timestamp,
    controllerTimestamp: firstDefined(unwrapped.controllerTimestamp, innerObj.timestamp, timestamp),
    created_at: createdAt,
    createdAt,
    observedAt: firstDefined(unwrapped.observedAt, unwrapped.observed_at, timestamp),
    receivedAt: firstDefined(unwrapped.receivedAt, unwrapped.received_at, createdAt),
    data: normalizedPayload,
    payload: normalizedPayload,
  };
};

const extractEnergyRecordList = (latestResponse) => {
  const unwrapped = unwrapEnvelope(latestResponse);
  if (!unwrapped) return [];
  if (Array.isArray(unwrapped)) return unwrapped;
  if (isObject(unwrapped)) {
    for (const key of ['data', 'items', 'observations', 'records', 'rows', 'results']) {
      if (Array.isArray(unwrapped[key])) return unwrapped[key];
    }
  }
  return [];
};

/**
 * Extract and normalize the latest canopy telemetry record for a single canopy
 * from an `/api/energy/latest` response or record list.
 */
export const extractCanopyTelemetryFromEnergyLatest = (
  latestResponse,
  canopyId,
  fallbackIdentity = {}
) => {
  const records = extractEnergyRecordList(latestResponse).filter(isCanopyTelemetryRecord);
  if (records.length === 0) return null;

  const sorted = [...records].sort((left, right) => recordTimestampMs(right) - recordTimestampMs(left));

  if (!canopyId) {
    return normalizeTelemetryRecord(sorted[0], fallbackIdentity);
  }

  const exactMatch = sorted.find((record) => (
    matchesCanopyId(record, canopyId, { allowNumberFallback: false })
  ));
  const matched = exactMatch || sorted.find((record) => (
    matchesCanopyId(record, canopyId, { allowNumberFallback: true })
  ));

  if (!matched) return null;

  return normalizeTelemetryRecord(matched, {
    ...fallbackIdentity,
    canopyId,
  });
};

/**
 * Extract a map `{ [canopyId]: normalizedRecord }` for multiple selected canopies
 * from an `/api/energy/latest` response.
 */
export const extractCanopyTelemetryMapFromEnergyLatest = (
  latestResponse,
  canopyIds = [],
  canopyNames = {}
) => {
  const result = {};
  canopyIds.forEach((canopyId) => {
    const normalized = extractCanopyTelemetryFromEnergyLatest(latestResponse, canopyId, {
      canopyId,
      canopyName: canopyNames[canopyId],
    });
    if (normalized) {
      result[canopyId] = normalized;
    }
  });
  return result;
};
